/**
 * The time axis two screens draw on.
 *
 * The stint chart and the planner have to read identically — the whole value of
 * a planner is holding it against what the car actually did — and they only do
 * that if the same span produces the same pixels and the same ticks in both.
 * Copying forty lines would have worked until one of them changed.
 */

// The subpath rather than the barrel: this module has a node:test suite, and
// the barrel drags in .tsx and Mantine, which node cannot load. clock.ts stands
// on its own, so it has its own export.
import { formatClock } from '@racedash/shared/clock'

export interface Span {
  from: number
  to: number
}

/**
 * Where an instant sits across the span, as a percentage.
 *
 * Clamped to the chart, because there is nowhere else for it to be. An instant
 * outside the span is either something spanning deliberately excluded — see
 * maxSpanMs — or something that genuinely runs past the edge, such as a pit
 * window closing after the last lap anybody completed. Both should be drawn at
 * the edge; neither should be handed to CSS as left: -49000000%.
 */
export function pct(at: number, span: Span): number {
  const width = span.to - span.from
  if (!(width > 0)) return 0
  return Math.min(100, Math.max(0, ((at - span.from) / width) * 100))
}

/**
 * The longest a session can plausibly be.
 *
 * The longest we support is the 25 Hours of Spa; a year is three hundred times
 * that. Anything further from the rest of the data than this is not a session
 * time, it is a corrupt one — a Go zero value reaching Firestore as year 1,
 * which is exactly what happened — and letting it set the axis is what turned
 * a qualifying session into a chart seventeen million hours wide.
 */
const maxSpanMs = 365 * 24 * 3_600_000

/**
 * Wide enough that a two-minute stint is still a readable card, and never so
 * wide that the browser has to find room for it.
 *
 * The cap is a fuse rather than a feature: spanning should already have thrown
 * out anything that could reach it. A chart pinned at the cap is unreadable,
 * but it is on screen and the tab still responds, which is the difference
 * between a bug you can see and a bug that takes the page with it.
 */
export function axisWidth(span: Span): number {
  const hours = (span.to - span.from) / 3_600_000
  return Math.min(200_000, Math.max(900, Math.round(hours * 420)))
}

export interface Tick {
  at: number
  label: string
  major: boolean
}

/**
 * How many ticks may be drawn, whatever the span says.
 *
 * The same fuse as axisWidth. A tick costs a DOM node and a
 * toLocaleTimeString, so an unbounded loop here does not degrade the page, it
 * stops it: a span of seventeen million hours asked for seventeen million of
 * them and the tab never came back.
 */
const maxTicks = 2_000

export function hourTicks(span: Span): Tick[] {
  const out: Tick[] = []
  const hours = (span.to - span.from) / 3_600_000
  // A tick every quarter hour reads as clutter across a 25-hour race and as
  // useful detail across a one-hour practice.
  const stepMs = hours > 6 ? 3_600_000 : hours > 2 ? 1_800_000 : 900_000

  const first = Math.ceil(span.from / stepMs) * stepMs
  for (let at = first; at <= span.to && out.length < maxTicks; at += stepMs) {
    const d = new Date(at)
    out.push({
      at,
      label: formatClock(d),
      major: d.getMinutes() === 0,
    })
  }
  return out
}

/**
 * A span covering everything given, or an hour up to now when nothing is.
 *
 * Everything it can believe, rather than literally everything. One stint in a
 * qualifying session carried an end of 0001-01-01 — a Go zero time written
 * through to Firestore — and an axis honest enough to include it was two
 * thousand years wide, which the browser could not draw.
 *
 * The newest instant is taken as the anchor because it is the one thing here
 * that is always real: it is either a lap that completed or the clock. Anything
 * more than a year older than it belongs to no session, and is dropped.
 */
export function spanning(instants: readonly number[], now: Date): Span {
  let to = -Infinity
  for (const n of instants) {
    // Not Math.max(...instants): a 25-hour race with a hundred cars is
    // thousands of stints, and spreading an array that size at an argument
    // list is a limit nobody remembers until it is hit.
    if (Number.isFinite(n) && n > to) to = n
  }

  let from = Infinity
  for (const n of instants) {
    if (Number.isFinite(n) && n < from && to - n <= maxSpanMs) from = n
  }

  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) {
    const t = now.getTime()
    return { from: t - 3_600_000, to: t }
  }
  return { from, to }
}
